import graphql from "babel-plugin-relay/macro";
import { useState } from "react";
import { useFragment } from "react-relay";

import {
  ScheduleOption,
  ScheduleSwitcher,
} from "@/components/ScheduleSwitcher";
import { useGetClubStartOfWeekNumberPatron } from "@/hooks/useGetClubStartOfWeekNumberPatron";
import {
  Box,
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { BookNow_session$key } from "./__generated__/BookNow_session.graphql";
import { BookNowCalendar } from "./components/BookNowCalendar";

type Props = {
  sessionRef: BookNow_session$key | null;
  onNavigate?: () => void;
  title: string;
  calendarOrientation?: "horizontal" | "vertical";
  twoMonthView?: boolean;
  syncSelectedOptionWithStoredDefault?: boolean;
};

export const BookNow = ({
  onNavigate,
  sessionRef,
  title,
  calendarOrientation = "vertical",
  twoMonthView = false,
  syncSelectedOptionWithStoredDefault = false,
}: Props) => {
  const session = useFragment(BookNowFragmentString, sessionRef);
  const [scheduleOption, setScheduleOption] = useState<ScheduleOption | null>(
    null
  );
  const onScheduleSwitcherChange = (option: ScheduleOption) => {
    setScheduleOption(option);
  };

  const clubWeekStartsOn = useGetClubStartOfWeekNumberPatron(
    session?.currentClientProfile?.member?.club ?? null
  );

  return (
    <VStack gap={8}>
      <Text testID="book-now-title" variant="mainHeading">
        {title}
      </Text>
      <HStack
        gap={calendarOrientation === "vertical" ? 6 : 3}
        flexDirection={calendarOrientation === "vertical" ? "column" : "row"}
      >
        <Box
          width={calendarOrientation === "vertical" ? "100%" : 344}
          mb={calendarOrientation === "vertical" ? 4 : 0}
        >
          <Box
            bg="surface-primary"
            p={0}
            width={{ base: "100%", lg: "50%" }}
            borderRadius="4"
          >
            <ScheduleSwitcher
              onChange={onScheduleSwitcherChange}
              selectedOption={scheduleOption}
              sessionRef={session ?? null}
              syncSelectedOptionWithStoredDefault={
                syncSelectedOptionWithStoredDefault
              }
            />
          </Box>
        </Box>
        {scheduleOption ? (
          <BookNowCalendar
            calendarOrientation={calendarOrientation}
            scheduleOption={scheduleOption}
            onNavigate={onNavigate}
            clubWeekStartsOn={clubWeekStartsOn}
            twoMonthView={twoMonthView}
          />
        ) : null}
      </HStack>
    </VStack>
  );
};

const BookNowFragmentString = graphql`
  fragment BookNow_session on Session {
    ...ScheduleSwitcher_session
    currentClientProfile {
      member {
        club {
          ...useGetClubStartOfWeekNumberPatron_club
        }
      }
    }
  }
`;
